import { lazy, type ComponentType, type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { LoginPage } from './features/auth/LoginPage';
import { SelectWorkspacePage } from './features/auth/SelectWorkspacePage';
import { RequirePermission, RequirePlatformSession, RequireSignedIn, RequireTenantSession } from './layout/guards';
import { PublicLayout } from './layout/PublicLayout';
import { PLATFORM_NAV, TENANT_NAV, type NavSection } from './lib/navigation';
import type { Requirement } from './lib/permissions';

/** React.lazy for named exports: every page is its own chunk. */
function lazyNamed<M, K extends keyof M>(load: () => Promise<M>, name: K): M[K] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return lazy(async () => ({ default: (await load())[name] as ComponentType<any> })) as M[K];
}

// Shells
const AppShell = lazyNamed(() => import('./layout/AppShell'), 'AppShell');
const AdminShell = lazyNamed(() => import('./layout/AdminShell'), 'AdminShell');
// Public / auth
const ForgotPasswordPage = lazyNamed(() => import('./features/auth/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('./features/auth/ResetPasswordPage'), 'ResetPasswordPage');
const AcceptInvitationPage = lazyNamed(() => import('./features/auth/AcceptInvitationPage'), 'AcceptInvitationPage');
// Tenant workspace
const DashboardPage = lazyNamed(() => import('./features/dashboard/DashboardPage'), 'DashboardPage');
const CompanyPage = lazyNamed(() => import('./features/settings/CompanyPage'), 'CompanyPage');
const UsersPage = lazyNamed(() => import('./features/settings/UsersPage'), 'UsersPage');
const RolesPage = lazyNamed(() => import('./features/settings/RolesPage'), 'RolesPage');
const ApprovalPolicyPage = lazyNamed(() => import('./features/settings/ApprovalPolicyPage'), 'ApprovalPolicyPage');
const NumberingPage = lazyNamed(() => import('./features/settings/NumberingPage'), 'NumberingPage');
const PaymentTermsPage = lazyNamed(() => import('./features/settings/PaymentTermsPage'), 'PaymentTermsPage');
const SupportAccessPage = lazyNamed(() => import('./features/settings/SupportAccessPage'), 'SupportAccessPage');
const AuditPage = lazyNamed(() => import('./features/settings/AuditPage'), 'AuditPage');
const SubscriptionPage = lazyNamed(() => import('./features/settings/SubscriptionPage'), 'SubscriptionPage');
const MySessionsPage = lazyNamed(() => import('./features/settings/MySessionsPage'), 'MySessionsPage');
// Business modules
const PartiesListPage = lazyNamed(() => import('./features/parties/PartiesListPage'), 'PartiesListPage');
const PartyFormPage = lazyNamed(() => import('./features/parties/PartyFormPage'), 'PartyFormPage');
const PartyDetailPage = lazyNamed(() => import('./features/parties/PartyDetailPage'), 'PartyDetailPage');
const ProductsListPage = lazyNamed(() => import('./features/products/ProductsListPage'), 'ProductsListPage');
const ProductFormPage = lazyNamed(() => import('./features/products/ProductFormPage'), 'ProductFormPage');
const ProductDetailPage = lazyNamed(() => import('./features/products/ProductDetailPage'), 'ProductDetailPage');
const DocumentsPage = lazyNamed(() => import('./features/documents/DocumentsPage'), 'DocumentsPage');
const OrdersListPage = lazyNamed(() => import('./features/orders/OrdersListPage'), 'OrdersListPage');
const OrderFormPage = lazyNamed(() => import('./features/orders/OrderFormPage'), 'OrderFormPage');
const OrderDetailPage = lazyNamed(() => import('./features/orders/OrderDetailPage'), 'OrderDetailPage');
const ApprovalsInboxPage = lazyNamed(() => import('./features/approvals/ApprovalsInboxPage'), 'ApprovalsInboxPage');
const PoListPage = lazyNamed(() => import('./features/procurement/PoListPage'), 'PoListPage');
const PoFormPage = lazyNamed(() => import('./features/procurement/PoFormPage'), 'PoFormPage');
const PoDetailPage = lazyNamed(() => import('./features/procurement/PoDetailPage'), 'PoDetailPage');
const BalancesPage = lazyNamed(() => import('./features/inventory/BalancesPage'), 'BalancesPage');
const MovementsPage = lazyNamed(() => import('./features/inventory/MovementsPage'), 'MovementsPage');
const LocationsPage = lazyNamed(() => import('./features/inventory/LocationsPage'), 'LocationsPage');
const BatchTracePage = lazyNamed(() => import('./features/inventory/BatchTracePage'), 'BatchTracePage');
const StockReconciliationPage = lazyNamed(() => import('./features/inventory/ReconciliationPage'), 'ReconciliationPage');
const ProformasListPage = lazyNamed(() => import('./features/finance/ProformasListPage'), 'ProformasListPage');
const ProformaDetailPage = lazyNamed(() => import('./features/finance/ProformaDetailPage'), 'ProformaDetailPage');
const PaymentsListPage = lazyNamed(() => import('./features/finance/PaymentsListPage'), 'PaymentsListPage');
const PaymentDetailPage = lazyNamed(() => import('./features/finance/PaymentDetailPage'), 'PaymentDetailPage');
const ReceivablesPage = lazyNamed(() => import('./features/finance/ReceivablesPage'), 'ReceivablesPage');
const ExpiryRiskPage = lazyNamed(() => import('./features/inventory/ExpiryRiskPage'), 'ExpiryRiskPage');
const ShipmentsPage = lazyNamed(() => import('./features/logistics/ShipmentsPage'), 'ShipmentsPage');
const ShipmentDetailPage = lazyNamed(() => import('./features/logistics/ShipmentDetailPage'), 'ShipmentDetailPage');
const PartnersPage = lazyNamed(() => import('./features/logistics/PartnersPage'), 'PartnersPage');
const PartnerDetailPage = lazyNamed(() => import('./features/logistics/PartnerDetailPage'), 'PartnerDetailPage');
const PayablesPage = lazyNamed(() => import('./features/logistics/PayablesPage'), 'PayablesPage');
const CarrierInvoiceDetailPage = lazyNamed(() => import('./features/logistics/CarrierInvoiceDetailPage'), 'CarrierInvoiceDetailPage');
const ReconciliationPage = lazyNamed(() => import('./features/logistics/ReconciliationPage'), 'ReconciliationPage');
const ReportsPage = lazyNamed(() => import('./features/reports/ReportsPage'), 'ReportsPage');
const AlertSettingsPage = lazyNamed(() => import('./features/settings/AlertSettingsPage'), 'AlertSettingsPage');
// Platform console
const TenantsPage = lazyNamed(() => import('./features/admin/TenantsPage'), 'TenantsPage');
const CreateTenantPage = lazyNamed(() => import('./features/admin/CreateTenantPage'), 'CreateTenantPage');
const TenantDetailPage = lazyNamed(() => import('./features/admin/TenantDetailPage'), 'TenantDetailPage');
const PlansPage = lazyNamed(() => import('./features/admin/PlansPage'), 'PlansPage');
const CountriesPage = lazyNamed(() => import('./features/admin/CountriesPage'), 'CountriesPage');
const UsagePage = lazyNamed(() => import('./features/admin/UsagePage'), 'UsagePage');
const PlatformAuditPage = lazyNamed(() => import('./features/admin/PlatformAuditPage'), 'PlatformAuditPage');
const WrongScopeNotice = lazyNamed(() => import('./features/admin/WrongScopeNotice'), 'WrongScopeNotice');
const NotFoundPage = lazyNamed(() => import('./features/NotFoundPage'), 'NotFoundPage');

/** Wrap a page in the same permission requirement its navigation item declares. */
function guarded(nav: readonly NavSection[], path: string, page: ReactNode): ReactNode {
  const item = nav.flatMap((s) => s.items).find((i) => i.to === path);
  return item?.requires ? <RequirePermission requires={item.requires}>{page}</RequirePermission> : page;
}
const t = (path: string, page: ReactNode) => guarded(TENANT_NAV, path, page);
const p = (path: string, page: ReactNode) => guarded(PLATFORM_NAV, path, page);
/** Section guard plus an extra requirement (create/edit pages need the manage permission too). */
const tx = (path: string, requires: Requirement, page: ReactNode) => t(path, <RequirePermission requires={requires}>{page}</RequirePermission>);

const business: RouteObject[] = [
  { path: 'customers', element: t('/customers', <PartiesListPage role="customer" />) },
  { path: 'customers/new', element: tx('/customers', { all: ['parties.manage'] }, <PartyFormPage role="customer" />) },
  { path: 'customers/:id', element: t('/customers', <PartyDetailPage role="customer" />) },
  { path: 'customers/:id/edit', element: tx('/customers', { all: ['parties.manage'] }, <PartyFormPage role="customer" />) },
  { path: 'manufacturers', element: t('/manufacturers', <PartiesListPage role="manufacturer" />) },
  { path: 'manufacturers/new', element: tx('/manufacturers', { all: ['parties.manage'] }, <PartyFormPage role="manufacturer" />) },
  { path: 'manufacturers/:id', element: t('/manufacturers', <PartyDetailPage role="manufacturer" />) },
  { path: 'manufacturers/:id/edit', element: tx('/manufacturers', { all: ['parties.manage'] }, <PartyFormPage role="manufacturer" />) },
  { path: 'products', element: t('/products', <ProductsListPage />) },
  { path: 'products/new', element: tx('/products', { all: ['products.manage'] }, <ProductFormPage />) },
  { path: 'products/:id', element: t('/products', <ProductDetailPage />) },
  { path: 'products/:id/edit', element: tx('/products', { all: ['products.manage'] }, <ProductFormPage />) },
  { path: 'documents', element: t('/documents', <DocumentsPage />) },
  { path: 'orders', element: t('/orders', <OrdersListPage />) },
  { path: 'orders/new', element: tx('/orders', { all: ['orders.create'] }, <OrderFormPage />) },
  { path: 'orders/:id', element: t('/orders', <OrderDetailPage />) },
  { path: 'orders/:id/edit', element: tx('/orders', { any: ['orders.create', 'orders.manage_all'] }, <OrderFormPage />) },
  { path: 'approvals', element: t('/approvals', <ApprovalsInboxPage />) },
  { path: 'purchase-orders', element: t('/purchase-orders', <PoListPage />) },
  { path: 'purchase-orders/new', element: tx('/purchase-orders', { all: ['procurement.manage'] }, <PoFormPage />) },
  { path: 'purchase-orders/:id', element: t('/purchase-orders', <PoDetailPage />) },
  { path: 'purchase-orders/:id/edit', element: tx('/purchase-orders', { all: ['procurement.manage'] }, <PoFormPage />) },
  { path: 'inventory', element: t('/inventory', <BalancesPage />) },
  { path: 'inventory/movements', element: t('/inventory', <MovementsPage />) },
  { path: 'inventory/locations', element: t('/inventory', <LocationsPage />) },
  { path: 'inventory/batches/:batchId', element: t('/inventory', <BatchTracePage />) },
  { path: 'inventory/reconciliation', element: tx('/inventory', { all: ['inventory.manage'] }, <StockReconciliationPage />) },
  { path: 'inventory/expiry', element: t('/inventory/expiry', <ExpiryRiskPage />) },
  { path: 'logistics', element: <Navigate to="/logistics/shipments" replace /> },
  { path: 'logistics/shipments', element: t('/logistics/shipments', <ShipmentsPage />) },
  { path: 'logistics/shipments/:id', element: t('/logistics/shipments', <ShipmentDetailPage />) },
  { path: 'logistics/exceptions', element: t('/logistics/exceptions', <ShipmentsPage variant="exceptions" />) },
  { path: 'logistics/partners', element: t('/logistics/partners', <PartnersPage />) },
  { path: 'logistics/partners/:id', element: t('/logistics/partners', <PartnerDetailPage />) },
  { path: 'logistics/reconciliation', element: t('/logistics/reconciliation', <ReconciliationPage />) },
  { path: 'finance/receivables', element: t('/finance/receivables', <ReceivablesPage />) },
  { path: 'finance/payables', element: t('/finance/payables', <PayablesPage />) },
  { path: 'finance/payables/:id', element: t('/finance/payables', <CarrierInvoiceDetailPage />) },
  { path: 'reports', element: t('/reports', <ReportsPage />) },
  { path: 'finance', element: <Navigate to="/finance/proformas" replace /> },
  { path: 'finance/proformas', element: t('/finance/proformas', <ProformasListPage />) },
  { path: 'finance/proformas/:id', element: t('/finance/proformas', <ProformaDetailPage />) },
  { path: 'finance/payments', element: t('/finance/payments', <PaymentsListPage />) },
  { path: 'finance/payments/:id', element: t('/finance/payments', <PaymentDetailPage />) },
];

/**
 * Route table. To add a module: create `src/features/<area>/`, add its pages to `business` (wrapped
 * with `t()`/`tx()` so the nav requirement also guards the route) and add a section to TENANT_NAV.
 */
export const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <LoginPage scope="tenant" /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/accept-invitation', element: <AcceptInvitationPage /> },
      { path: '/select-workspace', element: <RequireSignedIn>{(s) => <SelectWorkspacePage session={s} />}</RequireSignedIn> },
      { path: '/admin/login', element: <LoginPage scope="platform" /> },
    ],
  },
  {
    element: <RequireTenantSession />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          ...business,
          { path: 'settings', element: <Navigate to="/settings/sessions" replace /> },
          { path: 'settings/company', element: t('/settings/company', <CompanyPage />) },
          { path: 'settings/users', element: t('/settings/users', <UsersPage />) },
          { path: 'settings/roles', element: t('/settings/roles', <RolesPage />) },
          { path: 'settings/approval-policy', element: t('/settings/approval-policy', <ApprovalPolicyPage />) },
          { path: 'settings/numbering', element: t('/settings/numbering', <NumberingPage />) },
          { path: 'settings/payment-terms', element: t('/settings/payment-terms', <PaymentTermsPage />) },
          { path: 'settings/alerts', element: t('/settings/alerts', <AlertSettingsPage />) },
          { path: 'settings/support-access', element: t('/settings/support-access', <SupportAccessPage />) },
          { path: 'settings/audit', element: t('/settings/audit', <AuditPage />) },
          { path: 'settings/subscription', element: t('/settings/subscription', <SubscriptionPage />) },
          { path: 'settings/sessions', element: <MySessionsPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  {
    path: '/admin',
    element: <RequirePlatformSession wrongScope={<WrongScopeNotice />} />,
    children: [
      {
        element: <AdminShell />,
        children: [
          { index: true, element: <Navigate to="/admin/tenants" replace /> },
          { path: 'tenants', element: p('/admin/tenants', <TenantsPage />) },
          { path: 'tenants/new', element: <RequirePermission requires={{ all: ['platform.tenants.manage'] }}><CreateTenantPage /></RequirePermission> },
          { path: 'tenants/:tenantId', element: p('/admin/tenants', <TenantDetailPage />) },
          { path: 'plans', element: p('/admin/plans', <PlansPage />) },
          { path: 'countries', element: p('/admin/countries', <CountriesPage />) },
          { path: 'usage', element: p('/admin/usage', <UsagePage />) },
          { path: 'audit', element: p('/admin/audit', <PlatformAuditPage />) },
          { path: '*', element: <NotFoundPage home="/admin" /> },
        ],
      },
    ],
  },
];
